import { useState, useEffect } from "react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Ticket, Calendar, Download, TrendingUp, Users, DollarSign, PieChart } from "lucide-react";
import { useCommandEntries } from "@/hooks/useCommandEntries";
import { useRestaurant } from "@/hooks/useRestaurant";
import { exportEntradasToPDF, exportEntradasToExcel } from "@/lib/report-export";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PieChart as ReChartPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const COLORS = ["hsl(var(--primary))", "hsl(var(--secondary))", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

export default function RelatorioEntradasPage() {
  const [dateFrom, setDateFrom] = useState<Date>(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const { entries, isLoading, getReport, refetch } = useCommandEntries();
  const { activeRestaurant } = useRestaurant();

  useEffect(() => {
    refetch(format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"));
  }, [dateFrom, dateTo]);

  const report = getReport(
    format(dateFrom, "yyyy-MM-dd"),
    format(dateTo, "yyyy-MM-dd")
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handlePreset = (preset: string) => {
    const today = new Date();
    switch (preset) {
      case "today":
        setDateFrom(today);
        setDateTo(today);
        break;
      case "week":
        setDateFrom(subDays(today, 7));
        setDateTo(today);
        break;
      case "month":
        setDateFrom(startOfMonth(today));
        setDateTo(endOfMonth(today));
        break;
    }
  };

  const dateRange = `${format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} a ${format(dateTo, "dd/MM/yyyy", { locale: ptBR })}`;

  const filteredEntries = entries.filter((e) => {
    const entryDate = e.created_at.split("T")[0];
    return entryDate >= format(dateFrom, "yyyy-MM-dd") && entryDate <= format(dateTo, "yyyy-MM-dd");
  });

  const handleExportPDF = () => {
    exportEntradasToPDF(filteredEntries, report, dateRange, activeRestaurant?.name);
  };

  const handleExportExcel = () => {
    exportEntradasToExcel(filteredEntries, report, dateRange);
  };

  const pieData = report.byType.map((item) => ({
    name: item.name,
    value: item.amount,
    count: item.count,
  }));

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Ticket className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Relatório de Entradas</h1>
            <p className="text-sm text-muted-foreground">
              Acompanhe as entradas de comandas por período
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePreset("today")}
          >
            Hoje
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePreset("week")}
          >
            7 dias
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePreset("month")}
          >
            Mês
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Calendar className="h-4 w-4" />
                {format(dateFrom, "dd/MM", { locale: ptBR })} -{" "}
                {format(dateTo, "dd/MM", { locale: ptBR })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="flex">
                <div className="border-r p-2">
                  <div className="text-xs font-medium mb-2 px-2">De</div>
                  <CalendarComponent
                    mode="single"
                    selected={dateFrom}
                    onSelect={(d) => d && setDateFrom(d)}
                    locale={ptBR}
                  />
                </div>
                <div className="p-2">
                  <div className="text-xs font-medium mb-2 px-2">Até</div>
                  <CalendarComponent
                    mode="single"
                    selected={dateTo}
                    onSelect={(d) => d && setDateTo(d)}
                    locale={ptBR}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <Download className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="h-4 w-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Entradas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{report.totalEntries}</div>
            <p className="text-xs text-muted-foreground">comandas abertas no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(report.totalAmount)}</div>
            <p className="text-xs text-muted-foreground">arrecadado em entradas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                report.totalEntries > 0
                  ? report.totalAmount / report.totalEntries
                  : 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">por entrada</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Chart by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Entradas por Tipo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado no período
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ReChartPie>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                  />
                  <Legend />
                </ReChartPie>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Summary by Type */}
        <Card>
          <CardHeader>
            <CardTitle>Resumo por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {report.byType.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado no período
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.byType.map((item, index) => (
                    <TableRow key={item.name}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          {item.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{item.count}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento</CardTitle>
          <CardDescription>
            Todas as entradas registradas no período
          </CardDescription>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma entrada no período selecionado</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Comanda</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Modo</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {format(new Date(entry.created_at), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell className="font-medium">
                          {entry.command_number.padStart(3, "0")}
                        </TableCell>
                        <TableCell>{entry.entry_type_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {entry.entry_mode === "fixed"
                              ? "Fixa"
                              : "Consumação"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(entry.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              entry.status === "active"
                                ? "default"
                                : entry.status === "closed"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {entry.status === "active"
                              ? "Ativo"
                              : entry.status === "closed"
                              ? "Fechado"
                              : "Cancelado"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
